import { useState, useEffect } from "react";
import { C, Btn, Switch, FEATURE_IDS, isFeatureOn, Input } from "./ui";
import { authService } from "../services/authService";
import { supabase } from "../services/supabase";

export function AdminPanel() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [features, setFeatures] = useState({});
  const [nombreCampoEditar, setNombreCampoEditar] = useState("");
  const [emailInvitar, setEmailInvitar] = useState("");
  const [msgInvitar, setMsgInvitar] = useState("");
  const [tab, setTab] = useState("usuarios"); // "usuarios" | "acceso"

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const cargarUsuarios = async () => {
    try {
      const data = await authService.listarUsuarios();
      setUsuarios(data || []);
    } catch (e) {
      console.error("Error cargando usuarios:", e);
    } finally {
      setLoading(false);
    }
  };

  const guardarFeatures = async () => {
    try {
      const usuarioActual = authService.obtenerUsuario();
      const updates = { features };
      if (nombreCampoEditar.trim()) {
        updates.nombre_campo = nombreCampoEditar;
      }
      await authService.actualizarUsuario(editando, updates);
      setUsuarios(usuarios.map(u => u.id === editando ? { ...u, features, nombre_campo: nombreCampoEditar || u.nombre_campo } : u));

      // Si se editó el usuario actual, actualizar localStorage y forzar reload para sincronizar campos
      if (editando === usuarioActual.id) {
        const usuarioActualizado = { ...usuarioActual, ...updates };
        localStorage.setItem("usuario", JSON.stringify(usuarioActualizado));
        // Recargar para refrescar campos disponibles
        setTimeout(() => window.location.reload(), 500);
      }

      setEditando(null);
      setNombreCampoEditar("");
    } catch (e) {
      console.error("Error guardando features:", e);
    }
  };

  const [passwordInvitar, setPasswordInvitar] = useState("");
  const [accesosCampos, setAccesosCampos] = useState([]);
  const [usuarioPropietario, setUsuarioPropietario] = useState("");
  const [usuarioAcceso, setUsuarioAcceso] = useState("");
  const [msgAcceso, setMsgAcceso] = useState("");
  const [featuresAcceso, setFeaturesAcceso] = useState({});
  const [editandoAcceso, setEditandoAcceso] = useState(null);
  const [featuresEditando, setFeaturesEditando] = useState({});

  useEffect(() => {
    cargarAccesos();
  }, []);

  const cargarAccesos = async () => {
    try {
      const usuario = authService.obtenerUsuario();
      const data = await authService.obtenerAccesoCampos(usuario.id);
      setAccesosCampos(data || []);
    } catch (e) {
      console.error("Error cargando accesos:", e);
    }
  };

  const agregarAccesoNuevo = async () => {
    if (!usuarioPropietario || !usuarioAcceso) {
      setMsgAcceso("Selecciona ambos usuarios");
      return;
    }
    // Darse acceso al propio campo agregaba una entrada "compartida" duplicada
    // del campo propio en el selector, con el mismo id.
    if (usuarioPropietario === usuarioAcceso) {
      setMsgAcceso("✗ El dueño ya tiene acceso a su campo");
      return;
    }
    setMsgAcceso("Asignando...");
    try {
      // upsert (no insert): reasignar el mismo par actualiza el permiso en vez de
      // crear una fila duplicada. Requiere el UNIQUE de SCHEMA.sql.
      const { data, error } = await supabase
        .from("permisos_campos")
        .upsert([{
          usuario_propietario_id: usuarioPropietario,
          usuario_acceso_id: usuarioAcceso,
          permiso: "lectura",
          features: featuresAcceso
        }], { onConflict: "usuario_propietario_id,usuario_acceso_id" })
        .select()
        .single();

      if (error) throw error;
      setAccesosCampos([
        ...accesosCampos.filter(a => !(a.usuario_propietario_id === usuarioPropietario && a.usuario_acceso_id === usuarioAcceso)),
        data
      ]);
      setUsuarioPropietario("");
      setUsuarioAcceso("");
      setFeaturesAcceso({});
      setMsgAcceso("✓ Acceso asignado");
      setTimeout(() => setMsgAcceso(""), 3000);
    } catch (e) {
      setMsgAcceso("✗ " + (e.message || "Error"));
    }
  };

  const eliminarAcceso = async (propietarioId, accesoId) => {
    try {
      await authService.eliminarAccesoCampo(propietarioId, accesoId);
      setAccesosCampos(accesosCampos.filter(a => !(a.usuario_propietario_id === propietarioId && a.usuario_acceso_id === accesoId)));
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const guardarFeaturesAcceso = async () => {
    if (!editandoAcceso) return;
    try {
      const { error } = await supabase
        .from("permisos_campos")
        .update({ features: featuresEditando })
        .match({ usuario_propietario_id: editandoAcceso.usuario_propietario_id, usuario_acceso_id: editandoAcceso.usuario_acceso_id });

      if (error) throw error;
      setAccesosCampos(accesosCampos.map(a =>
        a.usuario_propietario_id === editandoAcceso.usuario_propietario_id && a.usuario_acceso_id === editandoAcceso.usuario_acceso_id
          ? { ...a, features: featuresEditando }
          : a
      ));
      setEditandoAcceso(null);
      setMsgAcceso("✓ Permisos actualizados");
      setTimeout(() => setMsgAcceso(""), 3000);
    } catch (e) {
      setMsgAcceso("✗ Error: " + (e.message || "Error"));
    }
  };

  const invitarUsuario = async () => {
    if (!emailInvitar.trim() || !passwordInvitar.trim()) {
      setMsgInvitar("Ingresá email y contraseña");
      return;
    }
    setMsgInvitar("Creando usuario...");
    try {
      const nuevoUsuario = await authService.crearUsuario(emailInvitar, passwordInvitar, "", "user");
      setUsuarios([...usuarios, nuevoUsuario]);
      setEmailInvitar("");
      setPasswordInvitar("");
      setMsgInvitar("✓ Usuario creado");
      setTimeout(() => setMsgInvitar(""), 3000);
    } catch (e) {
      setMsgInvitar("✗ " + (e.message || "Error"));
    }
  };

  if (loading) return <div style={{ padding: 20, color: C.muted }}>Cargando...</div>;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo, padding: "20px 24px" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "serif", fontSize: 22, color: C.tierra, fontWeight: 600, marginBottom: 4 }}>Administración</div>
        <div style={{ fontSize: 13, color: C.muted }}>Gestión de usuarios, permisos y accesos</div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, borderBottom: `1px solid ${C.arenaDark}`, paddingBottom: 12 }}>
        <button onClick={() => setTab("usuarios")} style={{
          background: tab === "usuarios" ? C.tierra : "transparent", color: tab === "usuarios" ? "#fff" : C.muted,
          border: "none", cursor: "pointer", padding: "8px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600
        }}>👥 Usuarios</button>
        <button onClick={() => setTab("acceso")} style={{
          background: tab === "acceso" ? C.tierra : "transparent", color: tab === "acceso" ? "#fff" : C.muted,
          border: "none", cursor: "pointer", padding: "8px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600
        }}>🔑 Acceso a Campos</button>
      </div>

      {tab === "usuarios" && (
      <div>
      {/* Crear usuario */}
      <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 20, border: `1px solid ${C.arenaDark}` }}>
        <div style={{ fontSize: 12, fontFamily: "monospace", textTransform: "uppercase", color: C.muted, marginBottom: 10, fontWeight: 600 }}>➕ Crear usuario</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
          <Input label="Email" type="email" value={emailInvitar} onChange={setEmailInvitar} placeholder="usuario@ejemplo.com" />
          <Input label="Contraseña" type="password" value={passwordInvitar} onChange={setPasswordInvitar} placeholder="••••••••" />
        </div>
        <Btn variant="primary" onClick={invitarUsuario} style={{ width: "100%" }}>Crear usuario</Btn>
        {msgInvitar && <div style={{ fontSize: 11, color: msgInvitar.includes("✓") ? C.ok : C.danger, marginTop: 6 }}>{msgInvitar}</div>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {/* Lista de usuarios */}
        <div>
          <div style={{ fontSize: 12, fontFamily: "monospace", textTransform: "uppercase", color: C.muted, marginBottom: 10, fontWeight: 600 }}>👥 Usuarios</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {usuarios.map(u => (
              <div key={u.id} onClick={() => { setEditando(u.id); setFeatures(u.features || {}); setNombreCampoEditar(u.nombre_campo || ""); }}
                style={{
                  background: editando === u.id ? C.tierra : C.white,
                  borderRadius: 8, padding: "10px 12px", cursor: "pointer", border: `1px solid ${C.arenaDark}`,
                  transition: "all 0.15s"
                }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: editando === u.id ? "#fff" : C.texto }}>{u.nombre_campo || u.email}</div>
                <div style={{ fontSize: 10, color: editando === u.id ? "rgba(255,255,255,0.7)" : C.muted, marginTop: 2 }}>{u.email}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Panel de edición */}
        {editando && (
          <div style={{ background: C.white, borderRadius: 12, padding: 16, border: `1px solid ${C.arenaDark}` }}>
            <Input label="Nombre del campo" value={nombreCampoEditar} onChange={setNombreCampoEditar} placeholder="Ej: El Trebolar" style={{ marginBottom: 16 }} />
            <div style={{ fontSize: 12, fontFamily: "monospace", textTransform: "uppercase", color: C.muted, marginBottom: 12, fontWeight: 600 }}>🔧 Módulos activos</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {FEATURE_IDS.map(id => (
                <div key={id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: C.texto }}>{id}</span>
                  <Switch
                    checked={isFeatureOn(features, id)}
                    onChange={(v) => setFeatures(f => ({ ...f, [id]: v }))}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <Btn size="sm" variant="ghost" onClick={() => setEditando(null)} style={{ flex: 1 }}>Cancelar</Btn>
              <Btn size="sm" variant="primary" onClick={guardarFeatures} style={{ flex: 1 }}>Guardar</Btn>
            </div>

          </div>
        )}
      </div>
      </div>
      )}

      {tab === "acceso" && (
      <div>
        {/* Crear nuevo acceso */}
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 20, border: `1px solid ${C.arenaDark}` }}>
          <div style={{ fontSize: 12, fontFamily: "monospace", textTransform: "uppercase", color: C.muted, marginBottom: 10, fontWeight: 600 }}>➕ Asignar acceso</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            <select value={usuarioPropietario} onChange={(e) => setUsuarioPropietario(e.target.value)}
              style={{ padding: "8px 10px", border: `1px solid ${C.arenaDark}`, borderRadius: 6, fontSize: 12 }}>
              <option value="">Propietario del campo...</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre_campo || u.email}</option>)}
            </select>
            <select value={usuarioAcceso} onChange={(e) => setUsuarioAcceso(e.target.value)}
              style={{ padding: "8px 10px", border: `1px solid ${C.arenaDark}`, borderRadius: 6, fontSize: 12 }}>
              <option value="">Usuario con acceso...</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre_campo || u.email}</option>)}
            </select>
          </div>

          {usuarioAcceso && (
            <div style={{ background: C.arena, borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontFamily: "monospace", textTransform: "uppercase", color: C.muted, marginBottom: 8, fontWeight: 600 }}>Módulos permitidos</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {FEATURE_IDS.map(id => (
                  <label key={id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                    <input type="checkbox" checked={isFeatureOn(featuresAcceso, id)} onChange={(e) => {
                      setFeaturesAcceso(f => ({ ...f, [id]: e.target.checked }));
                    }} style={{ cursor: "pointer" }} />
                    <span>{id}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <Btn variant="primary" onClick={agregarAccesoNuevo} style={{ width: "100%" }}>Asignar acceso</Btn>
          {msgAcceso && <div style={{ fontSize: 11, color: msgAcceso.includes("✓") ? C.ok : C.danger, marginTop: 8 }}>{msgAcceso}</div>}
        </div>

        {/* Tabla de accesos */}
        <div style={{ background: C.white, borderRadius: 12, padding: 16, border: `1px solid ${C.arenaDark}`, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontFamily: "monospace", textTransform: "uppercase", color: C.muted, marginBottom: 12, fontWeight: 600 }}>Accesos activos</div>
          {accesosCampos.length === 0 ? (
            <div style={{ fontSize: 12, color: C.muted, textAlign: "center", padding: 20 }}>Sin accesos asignados</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.arenaDark}` }}>
                    <th style={{ textAlign: "left", padding: 8, fontWeight: 600, color: C.tierra }}>Propietario</th>
                    <th style={{ textAlign: "left", padding: 8, fontWeight: 600, color: C.tierra }}>Usuario con acceso</th>
                    <th style={{ textAlign: "left", padding: 8, fontWeight: 600, color: C.tierra }}>Permiso</th>
                    <th style={{ textAlign: "center", padding: 8, fontWeight: 600, color: C.tierra }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {accesosCampos.map(a => (
                    <tr key={a.id} style={{ borderBottom: `1px solid ${C.arenaDark}`, background: editandoAcceso?.id === a.id ? C.arena : "transparent" }}>
                      <td style={{ padding: 8, color: C.texto }}>{usuarios.find(u => u.id === a.usuario_propietario_id)?.nombre_campo || "—"}</td>
                      <td style={{ padding: 8, color: C.texto }}>{usuarios.find(u => u.id === a.usuario_acceso_id)?.email || "—"}</td>
                      <td style={{ padding: 8, color: C.muted, fontSize: 11 }}>{a.permiso}</td>
                      <td style={{ padding: 8, textAlign: "center", display: "flex", gap: 4, justifyContent: "center" }}>
                        <button onClick={() => { setEditandoAcceso(a); setFeaturesEditando(a.features || {}); }}
                          style={{ background: "none", border: "none", color: C.tierra, cursor: "pointer", fontSize: 14 }}>✏️</button>
                        <button onClick={() => eliminarAcceso(a.usuario_propietario_id, a.usuario_acceso_id)}
                          style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 14 }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Editor de features */}
        {editandoAcceso && (
          <div style={{ background: C.arena, borderRadius: 12, padding: 16, border: `1px solid ${C.arenaDark}` }}>
            <div style={{ fontSize: 12, fontFamily: "monospace", textTransform: "uppercase", color: C.muted, marginBottom: 12, fontWeight: 600 }}>
              Editar permisos: {usuarios.find(u => u.id === editandoAcceso.usuario_acceso_id)?.email}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              {FEATURE_IDS.map(id => (
                <label key={id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={isFeatureOn(featuresEditando, id)} onChange={(e) => {
                    setFeaturesEditando(f => ({ ...f, [id]: e.target.checked }));
                  }} style={{ cursor: "pointer" }} />
                  <span>{id}</span>
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn size="sm" variant="ghost" onClick={() => setEditandoAcceso(null)} style={{ flex: 1 }}>Cancelar</Btn>
              <Btn size="sm" variant="primary" onClick={guardarFeaturesAcceso} style={{ flex: 1 }}>Guardar cambios</Btn>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
